
import Vue from "vue";
import {
  Contact,
  contactApi,
  ContactSearchRow,
} from "@/api/contacts";
import { ValidationProvider } from "vee-validate";
import { mapState } from "vuex";
import {Dossier, LanguageType, PackageInfo} from "@/api/flame";
import DateTimeTextFieldPicker from "@/components/DateTimeTextFieldPicker.vue";
import DateTextFieldPicker from "@/components/DateTextFieldPicker.vue";
import {
  contactRelationApi,
  ContactRelationResult,
} from "@/api/contactRelation";
import QuickContactSearch from "@/views/contacts/QuickContactSearch.vue";
import EventBus from "@/utils/EventBus";
import {ConfigureEmailNaviEvent, SelectedContactEvent } from "@/layout/Layout.vue";
import  ProductForm  from "@/views/flames/dossier/ProductForm.vue";
import  AvisForm  from "@/views/flames/dossier/AvisForm.vue";
import  DeliveryAddressForm  from "@/views/flames/dossier/DeliveryAddressForm.vue";
import  BillingAddressForm  from "@/views/flames/dossier/BillingAddressForm.vue";
import  PackageInfoForm  from "@/views/flames/dossier/PackageInfoForm.vue";
import {lockedPickupDateApi} from "@/api/misc";

export interface DossierFormInputDTO {
  dossier: Dossier | null;
  contact: Contact | null;
  taskId: string | null;
  blackBoardRef: string | null;
}

export default Vue.extend({
  components: {
    ValidationProvider,
    DateTextFieldPicker,
    DateTimeTextFieldPicker,
    QuickContactSearch,
    ProductForm,
    AvisForm,
    DeliveryAddressForm,
    BillingAddressForm,
    PackageInfoForm,
  },
  props: {
    dossierFormInput: {
      type: Object as () => DossierFormInputDTO,
      required: true,
    },
    readonly: {
      type: Boolean,
    },
  },
  data() {
    return {
      contact: {} as Contact,
      searchingContact: false,
      searchContactDialog: false,
      dossier: {} as Dossier,
      valid: true,
      isWorking: false,
      resellerContacts: [] as ContactRelationResult[],
      notAllowedDates: [] as Date[],
    };
  },
  computed: {
    languageTypes(): { value: string; text: string }[] {
      return Object.keys(LanguageType).map((k) => ({
        value: k,
        text: this.$t(`enum.languageType.${k}`) as string,
      }));
    },
    ...mapState("condition", ["deliveryConditions"]),
    ...mapState("condition", ["paymentConditions"]),
    ...mapState("forwardingAgency", ["forwardingAgency"]),
  },
  watch: {
    dossierFormInput: {
      async handler() {
        const resetRefs = this.contact !== null && this.contact !== undefined && this.contact.id !== undefined && this.dossierFormInput.contact?.id !== undefined && this.contact.id !== this.dossierFormInput.contact.id
        if (this.dossierFormInput.contact !== null && this.dossierFormInput.contact?.id !== undefined) {
          this.contact = await contactApi.getById(
            this.dossierFormInput.contact.id
          );

        }
        if (this.dossierFormInput.dossier) {
          this.dossier = this.dossierFormInput.dossier;
          if(resetRefs) {
            this.dossier.avisPhoneNumberRef = null;
            this.dossier.avisEmailRef = null;
            this.dossier.deliveryAddressRef = null;
            this.dossier.billingAddressRef = null;
          }
          await this.init();
        }
        this.notAllowedDates = (await lockedPickupDateApi.list()).map(value => value.lockedAt ?? new Date(0));
      },
      immediate: true,
    },
  },
  async created() {
    await this.$store.dispatch("condition/listAll");
  },
  methods: {
    getDossierTitle() {
      if( this.contact ) {
        return this.$t("Dossier") + " " + this.contact.fullName + " - " + (this.contact.customerType ? this.$t("enum.customerType." + this.contact.customerType) : "Unbekannt");
      }
      return "";
    },
    async changeContact() {
      this.$emit("changeContact");
    },
    async jumpToContact(contactId: string) {
      await this.$store.dispatch(
        "navigation/addHistory",
        "/flames/" +
          this.dossierFormInput.taskId +
          "/" +
          this.dossierFormInput.blackBoardRef
      );
      await this.$router.push(`/contacts/${contactId}`);
    },
    async addResellerToDossier(contactSearchRow: ContactSearchRow) {
      if (this.dossier) {
        this.dossier.resellerRef = contactSearchRow.id;
        this.searchContactDialog = false;
      }
    },
    async init() {
      if (this.contact && this.contact.id) {
        console.log("init", this.contact)
        EventBus.$emit("selectedContact", { contact: this.contact } as SelectedContactEvent );
        this.resellerContacts = await contactRelationApi.listResellerCustomers(
          this.contact.id ?? ""
        );
        if( this.contact.emailAddresses && this.contact.emailAddresses.length > 0) {
          EventBus.$emit("configureEmailNavi", {recipients: this.contact.emailAddresses} as ConfigureEmailNaviEvent);
        }
        if (this.dossier.id == null) {
          this.dossier.certificateName = this.contact.fullName ?? "Unbekannt";
        }
      }
    },
    async updateDossier() {
      if(this.contact != null || this.contact !== undefined) {
        EventBus.$emit("selectedContact", {contact: this.contact} as SelectedContactEvent);
        EventBus.$emit("configureEmailNavi", {recipients: this.contact.emailAddresses} as ConfigureEmailNaviEvent);
      }
      this.$emit("dossierChanged", this.dossier);
    },
    async changeDeliveryAddress(addressRef: string | null) {
      if( this.dossier.deliveryAddressRef !== addressRef) {
        this.dossier.deliveryAddressRef = addressRef;
        await this.refreshContactAndResellers();
        await this.updateDossier();
      }
    },
    async changeBillingAddress(addressRef: string | null) {
      if( this.dossier.billingAddressRef !== addressRef) {
        this.dossier.billingAddressRef = addressRef;
        await this.refreshContactAndResellers();
        await this.updateDossier();
      }
    },
    async updateAvisEmailRef(ref: string | null) {
     if( this.dossier.avisEmailRef !== ref) {
       this.dossier.avisEmailRef = ref;
       await this.refreshContactAndResellers();
       await this.updateDossier();
     }
   },
    async updateAvisPhoneNumberRef(ref: string | null) {
      if( this.dossier.avisPhoneNumberRef !== ref) {
        this.dossier.avisPhoneNumberRef = ref;
        await this.refreshContactAndResellers();
        await this.updateDossier();
      }
    },
    async updateAvisText(text: string | null) {
      if( this.dossier.avisText !== text) {
        this.dossier.avisText = text;
        await this.updateDossier();
      }
    },
    async updateWithPackageInfos(packageInfos: PackageInfo[]) {
      this.dossier.packageInfos = packageInfos;
      await this.updateDossier();
    },
    async refreshContactAndResellers() {
      if( this.contact ) {
        this.contact = await contactApi.getById(
            this.contact.id
        );
        this.resellerContacts = await contactRelationApi.listResellerCustomers(
            this.contact.id ?? ""
        );
      }
    },
  },
});
